// Import css reset here to make sure it's included before
// any other Pluma styles
import { template } from "@ember/template-compiler";
import '@/css/reset.css.ts';
import Component from '@glimmer/component';
import type { IconManager } from '@customerio/pluma-icons';
import type { PlumaTheme, PartialPlumaTheme } from '@/css/themes/pluma-theme';
import ThemeContextProvider, { PlumaThemeContext } from './-private/theme-context';
import ThemeContainer from './-private/theme-container';
import ContextProvider from 'ember-provide-consume-context/components/context-provider';
import { consume } from 'ember-provide-consume-context';
import type ContextRegistry from 'ember-provide-consume-context/context-registry';
import { merge } from 'lodash-es';
import { makePlumaContextValue } from '../pluma-context';
import type { PlumaComponentFlags, PlumaContextValue } from '../pluma-context';
export { PlumaContextValue };
export const PlumaContext = 'pluma-context' as const;
export interface PlumaProviderSignature {
    // The Element isn't quite correct: if `shouldRenderContainer` is false,
    // there is no element. Glint doesn't seem to support this sort of
    // conditional element yet.
    Element: HTMLDivElement;
    Args: {
        theme?: PlumaTheme | PartialPlumaTheme;
        iconManager?: IconManager;
        shouldRenderContainer?: boolean;
        componentFlags?: PlumaComponentFlags;
    };
    Blocks: {
        default: [];
    };
}
export default class PlumaProvider extends Component<PlumaProviderSignature> {
    @consume(PlumaContext)
    existingPlumaContext: ContextRegistry[typeof PlumaContext];
    @consume(PlumaThemeContext)
    contextTheme: ContextRegistry[typeof PlumaThemeContext];
    get plumaContextValue() {
        const { existingPlumaContext: existingPlumaContext1 } = this;
        return makePlumaContextValue(merge({}, existingPlumaContext1, {
            componentFlags: this.args.componentFlags ?? undefined
        }));
    }
    iconManager?: IconManager;
    constructor(owner1: unknown, args1: PlumaProviderSignature['Args']){
        super(owner1, args1);
        this.iconManager = args1.iconManager;
        if (this.iconManager != null) {
            this.iconManager.attachSpritesheet();
        }
    }
    get theme() {
        let { theme: theme1 } = this.args;
        const { contextTheme: contextTheme1 } = this;
        if (theme1 != null && contextTheme1 != null) {
            theme1 = {
                // Merge names so we can keep track of the hierarchy of applied themes
                name: `${theme1.name}_${contextTheme1.name}`,
                // No need to merge class names, because the parent theme is already
                // applied at this point
                className: theme1.className,
                // Merge tokens and styles, so that child context consumers have all
                // values available
                tokens: merge({}, contextTheme1.tokens, theme1.tokens),
                styles: merge({}, contextTheme1.styles, theme1.styles)
            };
        } else if (theme1 == null && contextTheme1 != null) {
            theme1 = contextTheme1;
        }
        return theme1;
    }
    get shouldRenderContainer() {
        return this.args.shouldRenderContainer ?? true;
    }
    static{
        template(`<ContextProvider @key={{PlumaContext}} @value={{this.plumaContextValue}}><ThemeContextProvider @theme={{this.theme}}>
		{{~#if this.shouldRenderContainer~}}
			<ThemeContainer @theme={{this.theme}} ...attributes>
				{{~yield~}}
			</ThemeContainer>
		{{~else~}}
			{{~yield~}}
		{{~/if~}}
	</ThemeContextProvider></ContextProvider>`, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module 'ember-provide-consume-context/context-registry' {
    export default interface ContextRegistry {
        [PlumaContext]: PlumaContextValue;
    }
}
